import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { getAnalyticsReport } from "@/lib/admin.functions";
import { DateRangePicker, defaultRange, type Range } from "@/components/admin/DateRange";
import { SectionTabs, ANALYTICS_TABS } from "@/components/admin/SectionTabs";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export const Route = createFileRoute("/_authenticated/admin/analytics")({
  component: AnalyticsPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-20 text-center">
        <p className="text-red-400 mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="text-zinc-400">Not found.</div>,
});

function AnalyticsPage() {
  const fn = useServerFn(getAnalyticsReport);
  const [range, setRange] = useState<Range>(defaultRange(30));
  const { data, isLoading } = useQuery({ queryKey: ["admin-analytics", range], queryFn: () => fn({ data: range }) });

  return (
    <div className="space-y-6">
      <SectionTabs tabs={ANALYTICS_TABS} label="Analytics" />
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-amber-300 text-xs font-semibold tracking-widest uppercase">Analytics</p>
          <h1 className="font-serif text-3xl text-white mt-1">Traffic & Behavior</h1>
        </div>
        <DateRangePicker value={range} onChange={setRange} />
      </header>

      {isLoading || !data ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Card label="Sessions" value={data.totals.sessions.toLocaleString()} />
            <Card label="Unique Visitors" value={data.totals.uniqueVisitors.toLocaleString()} />
            <Card label="Pageviews" value={data.totals.pageviews.toLocaleString()} />
            <Card label="Avg. Duration" value={`${Math.floor(data.totals.avgDuration / 60)}m ${data.totals.avgDuration % 60}s`} />
            <Card label="Bounce Rate" value={`${data.totals.bounceRate.toFixed(1)}%`} />
          </div>

          <TrendChart title="Daily traffic" data={data.daily as any} />

          <div className="grid lg:grid-cols-2 gap-4">
            <BarPanel title="Top landing pages" rows={data.landing} />
            <BarPanel title="Top exit pages" rows={data.exits} />
            <DonutPanel title="Traffic sources" rows={data.utm} />
            <DonutPanel title="Campaigns" rows={data.campaigns} />
            <DonutPanel title="Devices" rows={data.devices} />
            <DonutPanel title="Browsers" rows={data.browsers} />
            <DonutPanel title="Operating systems" rows={data.os} />
            <BarPanel title="Countries" rows={data.countries} />
          </div>

          <ScrollDepthPanel rows={data.scrollDepth as any} />
        </>
      )}
    </div>
  );
}

function Spinner() { return <div className="py-20 grid place-items-center"><Loader2 className="size-5 animate-spin text-zinc-500" /></div>; }
function Card({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <p className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="text-2xl text-white font-serif mt-1">{value}</p>
    </div>
  );
}

const AMBER = ["#fcd34d", "#f59e0b", "#fbbf24", "#d97706", "#fde68a", "#b45309", "#fef3c7", "#92400e"];

function ChartShell({ title, children, empty }: { title: string; children: React.ReactNode; empty?: boolean }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <h3 className="text-sm font-semibold text-white mb-3">{title}</h3>
      {empty ? <p className="text-xs text-zinc-500 py-12 text-center">No data yet</p> : children}
    </div>
  );
}

const tooltipStyle = {
  contentStyle: {
    background: "rgba(10,10,10,0.95)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 10,
    fontSize: 12,
    color: "#fff",
  },
  labelStyle: { color: "#a1a1aa", fontSize: 11 },
  cursor: { fill: "rgba(252,211,77,0.06)" },
};

function fmtDate(d: string) {
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function TrendChart({ title, data }: { title: string; data: { date: string; pageviews: number; sessions: number }[] }) {
  return (
    <ChartShell title={title} empty={!data?.length}>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gPv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fcd34d" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#fcd34d" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gSe" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#a78bfa" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#52525b" tick={{ fontSize: 10 }} />
            <YAxis stroke="#52525b" tick={{ fontSize: 10 }} allowDecimals={false} />
            <Tooltip {...tooltipStyle} labelFormatter={(l) => fmtDate(String(l))} />
            <Area type="monotone" dataKey="pageviews" stroke="#fcd34d" strokeWidth={2} fill="url(#gPv)" />
            <Area type="monotone" dataKey="sessions" stroke="#a78bfa" strokeWidth={2} fill="url(#gSe)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="flex gap-4 mt-2 text-[11px] text-zinc-400 justify-end">
        <span className="inline-flex items-center gap-1.5"><i className="size-2 rounded-full bg-amber-300 inline-block" />Pageviews</span>
        <span className="inline-flex items-center gap-1.5"><i className="size-2 rounded-full bg-violet-400 inline-block" />Sessions</span>
      </div>
    </ChartShell>
  );
}

function BarPanel({ title, rows }: { title: string; rows: { name: string; count: number }[] }) {
  const data = rows.slice(0, 8);
  return (
    <ChartShell title={title} empty={!data.length}>
      <div style={{ height: Math.max(180, data.length * 28) }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
            <XAxis type="number" stroke="#52525b" tick={{ fontSize: 10 }} allowDecimals={false} />
            <YAxis type="category" dataKey="name" stroke="#a1a1aa" tick={{ fontSize: 11 }} width={120} tickFormatter={(v) => (v.length > 18 ? v.slice(0, 17) + "…" : v)} />
            <Tooltip {...tooltipStyle} />
            <Bar dataKey="count" fill="#fcd34d" radius={[0, 6, 6, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartShell>
  );
}

function DonutPanel({ title, rows }: { title: string; rows: { name: string; count: number }[] }) {
  const data = rows.slice(0, 6);
  const total = data.reduce((s, r) => s + r.count, 0);
  return (
    <ChartShell title={title} empty={!data.length}>
      <div className="flex items-center gap-4">
        <div className="w-40 h-40 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="count" nameKey="name" innerRadius={42} outerRadius={70} paddingAngle={2} stroke="none">
                {data.map((_, i) => <Cell key={i} fill={AMBER[i % AMBER.length]} />)}
              </Pie>
              <Tooltip {...tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className="flex-1 space-y-1.5 text-xs min-w-0">
          {data.map((r, i) => (
            <li key={r.name} className="flex items-center justify-between gap-2 text-zinc-300">
              <span className="inline-flex items-center gap-2 min-w-0">
                <i className="size-2 rounded-full shrink-0" style={{ background: AMBER[i % AMBER.length] }} />
                <span className="truncate">{r.name}</span>
              </span>
              <span className="text-zinc-500 shrink-0">{total ? Math.round((r.count / total) * 100) : 0}%</span>
            </li>
          ))}
        </ul>
      </div>
    </ChartShell>
  );
}

function ScrollDepthPanel({ rows }: { rows: { path: string; avg: number; n: number }[] }) {
  return (
    <ChartShell title="Avg. scroll depth (%)" empty={!rows.length}>
      <ul className="space-y-2.5">
        {rows.map((r) => (
          <li key={r.path} className="text-xs">
            <div className="flex justify-between text-zinc-300 mb-1">
              <span className="truncate pr-2 font-mono text-zinc-400">{r.path}</span>
              <span className="text-amber-300 font-semibold">{r.avg}%<span className="text-zinc-400 font-normal ml-2">{r.n} samples</span></span>
            </div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-400 to-amber-200 rounded-full" style={{ width: `${Math.min(100, r.avg)}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </ChartShell>
  );
}